import { createFileRoute } from "@tanstack/react-router";
import fatherSon640 from "@/assets/father-son-sunset-640.webp";
import fatherSon1024 from "@/assets/father-son-sunset-1024.webp";
import fatherSon1600 from "@/assets/father-son-sunset-1600.webp";
import fatherSon1920 from "@/assets/father-son-sunset-1920.webp";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  ArrowDown,
  Check,
  ShieldCheck,
  Heart,
  Compass,
  Hammer,
  Flame,
  Crown,
  Gift,
  Sparkles,
  Clock,
  Users,
} from "lucide-react";

const LeadCaptureModal = lazy(() => import("@/components/lead-capture-modal"));

export const Route = createFileRoute("/paimemoravel")({
  head: () => ({
    meta: [
      { title: "O Pai que Seu Filho Vai Lembrar™ — Método Presença Que Marca" },
      {
        name: "description",
        content:
          "Programa para empresários cristãos que desejam construir uma relação forte, admirada e memorável com seus filhos. Sem trabalhar menos. Por apenas R$497.",
      },
      { property: "og:title", content: "O Pai que Seu Filho Vai Lembrar™" },
      {
        property: "og:description",
        content:
          "Daqui a 20 anos, o que seu filho vai lembrar sobre você? Descubra o Método Presença Que Marca.",
      },
      { property: "og:type", content: "product" },
      { property: "og:url", content: "https://presencaquemarca.com.br/paimemoravel" },
      { property: "og:image", content: `https://presencaquemarca.com.br${fatherSon1600}` },
      { name: "twitter:image", content: `https://presencaquemarca.com.br${fatherSon1600}` },
    ],
    links: [
      { rel: "canonical", href: "https://presencaquemarca.com.br/paimemoravel" },
      // Preload the LCP hero (WebP srcset) so it loads in parallel with critical CSS.
      {
        rel: "preload",
        as: "image",
        href: fatherSon1024,
        imageSrcSet: `${fatherSon640} 640w, ${fatherSon1024} 1024w, ${fatherSon1600} 1600w, ${fatherSon1920} 1920w`,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
  }),
  component: PaiMemoravelPage,
});

// Global helper so any CTA (including the fixed mobile bar) can open the modal.
let openLeadModalFn: (() => void) | null = null;
function openLeadModal() {
  if (openLeadModalFn) openLeadModalFn();
}

function GoldButton({
  children,
  large = false,
  dataEvent = "cta_comprar_pai_memoravel",
}: {
  children: React.ReactNode;
  large?: boolean;
  dataEvent?: string;
}) {
  return (
    <button
      type="button"
      onClick={openLeadModal}
      data-event={dataEvent}
      className={`group relative inline-flex items-center justify-center gap-3 rounded-sm gradient-gold font-sans font-semibold uppercase tracking-[0.18em] text-navy-deep shadow-gold transition-all hover:scale-[1.02] hover:shadow-[0_25px_70px_-15px_oklch(0.78_0.13_82_/_0.7)] ${
        large ? "px-8 py-5 text-sm md:px-12 md:py-6 md:text-base" : "px-6 py-4 text-xs md:px-8 md:py-4 md:text-sm"
      }`}
    >
      <span>{children}</span>
      <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-6 flex items-center justify-center gap-3">
      <span className="h-px w-10 bg-gold/60" />
      <span className="font-sans text-[11px] uppercase tracking-[0.4em] text-gold">{children}</span>
      <span className="h-px w-10 bg-gold/60" />
    </div>
  );
}

function PaiMemoravelPage() {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    openLeadModalFn = () => setModalOpen(true);
    return () => {
      openLeadModalFn = null;
    };
  }, []);

  return (
    <main className="overflow-x-hidden bg-background text-foreground">
      {/* HERO */}
      <section className="relative min-h-[100svh] w-full overflow-hidden bg-navy-deep">
        <img
          src={fatherSon1024}
          srcSet={`${fatherSon640} 640w, ${fatherSon1024} 1024w, ${fatherSon1600} 1600w, ${fatherSon1920} 1920w`}
          sizes="100vw"
          alt="Pai caminhando de mãos dadas com seu filho ao pôr do sol"
          width={1920}
          height={1280}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 via-navy-deep/55 to-navy-deep" />
        <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-5xl flex-col items-center justify-center px-6 py-24 text-center">
          <div className="animate-fade-up">
            <SectionLabel>Para Pais Empresários Cristãos</SectionLabel>
            <h1 className="font-display text-4xl font-light leading-[1.1] text-ivory md:text-6xl lg:text-7xl">
              Daqui a 20 anos,
              <br />
              <span className="italic text-gradient-gold">o que seu filho</span>
              <br />
              vai lembrar sobre você?
            </h1>
            <p className="mx-auto mt-8 max-w-2xl text-base font-light leading-relaxed text-ivory/80 md:text-lg">
              Você não precisa trabalhar menos para ser um pai memorável. Descubra como empresários
              cristãos estão fortalecendo a conexão, a influência e o relacionamento com seus filhos
              através do <span className="text-gold">Método Presença Que Marca™</span>.
            </p>
            <div className="mt-10">
              <GoldButton large>Quero me tornar um pai memorável</GoldButton>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-scroll-bounce">
            <ArrowDown className="h-6 w-6 text-gold" />
          </div>
        </div>
      </section>

      {/* DOR */}
      <section className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <SectionLabel>Uma Pergunta Difícil</SectionLabel>
          <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
            Você está construindo uma empresa admirável...
          </h2>
          <p className="mt-4 font-display text-xl italic text-muted-foreground md:text-2xl">
            Mas está construindo um relacionamento admirável com seus filhos?
          </p>

          <ul className="mx-auto mt-14 max-w-xl space-y-5 text-left">
            {[
              "Seu filho conversa menos com você.",
              "O tempo está passando rápido demais.",
              "Sua influência está diminuindo.",
              "Você está presente fisicamente, mas distante emocionalmente.",
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-4 border-b border-border/60 pb-5 text-base text-navy/85 md:text-lg"
              >
                <Check className="mt-1 h-5 w-5 flex-shrink-0 text-gold" strokeWidth={2.5} />
                <span className="font-light">{item}</span>
              </li>
            ))}
          </ul>

          <blockquote className="mt-16 border-l-2 border-gold pl-6 text-left md:pl-8">
            <p className="font-display text-2xl italic leading-snug text-navy md:text-3xl">
              "Estou construindo patrimônio enquanto perco momentos que nunca voltarão?"
            </p>
          </blockquote>
        </div>
      </section>

      {/* INIMIGO */}
      <section className="bg-navy-deep px-6 py-24 text-ivory md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <SectionLabel>O Verdadeiro Inimigo</SectionLabel>
          <h2 className="font-display text-3xl font-light leading-tight md:text-5xl">
            O erro que a maioria dos
            <br />
            <span className="italic text-gradient-gold">pais empresários</span> comete
          </h2>

          <div className="mx-auto mt-14 max-w-2xl rounded-sm border border-gold/30 bg-navy/40 p-10 backdrop-blur-sm shadow-premium md:p-14">
            <div className="mb-4 font-sans text-[11px] uppercase tracking-[0.4em] text-gold">
              Diagnóstico
            </div>
            <h3 className="font-display text-3xl font-medium text-gradient-gold md:text-4xl">
              Síndrome do Pai Provedor™
            </h3>
            <p className="mt-6 font-display text-xl italic text-ivory/90 md:text-2xl">
              "A crença de que sustentar é suficiente."
            </p>
          </div>

          <div className="mx-auto mt-14 flex max-w-xl flex-col items-center gap-4 md:flex-row md:justify-center md:gap-8">
            <span className="font-display text-xl text-ivory/90 md:text-2xl">Provisão Financeira</span>
            <span className="text-3xl font-light text-gold md:text-4xl">≠</span>
            <span className="font-display text-xl text-gold md:text-2xl">Influência Paterna</span>
          </div>
        </div>
      </section>

      {/* NOVA OPORTUNIDADE */}
      <section className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-5xl text-center">
          <SectionLabel>Uma Nova Forma de Enxergar</SectionLabel>
          <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
            Seu filho não mede sua presença
            <br />
            <span className="italic text-gold">em horas.</span>
          </h2>
          <p className="mt-4 font-display text-xl text-muted-foreground md:text-2xl">
            Ele mede sua presença em <span className="italic text-navy">momentos</span>.
          </p>

          <div className="mt-16 inline-block rounded-sm border border-gold/40 px-6 py-3">
            <span className="font-sans text-[11px] uppercase tracking-[0.4em] text-gold">
              Apresentamos
            </span>
            <div className="mt-1 font-display text-xl font-medium text-navy md:text-2xl">
              Índice de Presença Memorável™ (IPM)
            </div>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {[
              { icon: Heart, title: "Atenção", desc: "Estar inteiro quando estiver presente." },
              { icon: Flame, title: "Emoção", desc: "Tocar o coração antes da agenda." },
              { icon: Sparkles, title: "Repetição", desc: "Construir rituais que viram memórias." },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="group rounded-sm border border-border bg-card p-10 transition-all hover:border-gold hover:shadow-premium"
              >
                <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-gold/50 bg-gold/10">
                  <Icon className="h-6 w-6 text-gold" />
                </div>
                <h3 className="font-display text-2xl text-navy">{title}</h3>
                <p className="mt-3 text-sm font-light text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUÇÃO */}
      <section className="gradient-navy px-6 py-24 text-ivory md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <SectionLabel>Conheça o Treinamento</SectionLabel>
          <h2 className="font-display text-4xl font-light italic leading-tight text-gradient-gold md:text-6xl">
            O Pai que Seu Filho
            <br />
            Vai Lembrar™
          </h2>
          <p className="mx-auto mt-8 max-w-2xl text-base font-light leading-relaxed text-ivory/80 md:text-lg">
            Um programa prático para empresários cristãos que desejam construir uma relação forte,
            admirada e memorável com seus filhos.
          </p>

          <ul className="mx-auto mt-12 max-w-xl space-y-4 text-left">
            {[
              "Criar conexão verdadeira",
              "Ganhar influência sem autoritarismo",
              "Fortalecer a confiança dos filhos",
              "Construir memórias duradouras",
              "Deixar um legado além do patrimônio",
            ].map((item) => (
              <li key={item} className="flex items-start gap-4 text-ivory/90">
                <Check className="mt-1 h-5 w-5 flex-shrink-0 text-gold" strokeWidth={2.5} />
                <span className="font-light md:text-lg">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* MÓDULOS */}
      <section className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <SectionLabel>A Jornada</SectionLabel>
            <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
              Os <span className="italic text-gold">5 Movimentos</span> do Método
            </h2>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-5">
            {[
              { n: "01", title: "Conectar", icon: Heart },
              { n: "02", title: "Influenciar", icon: Compass },
              { n: "03", title: "Formar", icon: Hammer },
              { n: "04", title: "Marcar", icon: Flame },
              { n: "05", title: "Legar", icon: Crown },
            ].map(({ n, title, icon: Icon }) => (
              <div
                key={n}
                className="group relative overflow-hidden rounded-sm border border-border bg-card p-8 transition-all hover:-translate-y-1 hover:border-gold hover:shadow-premium"
              >
                <div className="font-display text-5xl font-light text-gold/30 transition-colors group-hover:text-gold/60">
                  {n}
                </div>
                <Icon className="mt-6 h-8 w-8 text-gold" strokeWidth={1.5} />
                <h3 className="mt-4 font-display text-2xl text-navy">{title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BÔNUS */}
      <section className="bg-muted/60 px-6 py-24 md:py-32">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <SectionLabel>Bônus Exclusivos</SectionLabel>
            <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
              Você também <span className="italic text-gold">recebe</span>
            </h2>
            <p className="mt-4 text-sm font-light text-muted-foreground md:text-base">
              R$ 107,60 cada — <span className="font-medium text-navy">R$ 538,00 em bônus</span> incluídos hoje.
            </p>
          </div>

          <div className="mt-16 grid gap-5 md:grid-cols-2">
            {[
              {
                n: "Bônus 1",
                title: "Cartas de Legado™",
                desc: "Guia com livro digital e caderno de aplicação para transformar sentimentos, valores, histórias, fé, princípios e aprendizados em cartas significativas para os filhos, integrado ao Método IPM™ — Presença que Marca™.",
              },
              {
                n: "Bônus 2",
                title: "Conversas que Formam Homens™",
                desc: "Guia com livro digital e baralho imprimível de perguntas e convites para conversas significativas com filhos de 4 a 15 anos, promovendo abertura, confiança, escuta, conexão, princípios, caráter, fé e direção.",
              },
              {
                n: "Bônus 3",
                title: "Diagnóstico IPM™",
                desc: "Livro digital e caderno de avaliação baseado nos cinco movimentos Enxergar, Conectar, Marcar, Modelar e Direcionar, identificando forças, vulnerabilidades e prioridades de transformação.",
              },
              {
                n: "Bônus 4",
                title: "Kit 52 Experiências Pai & Filho™",
                desc: "Livro digital de 64 páginas e baralho imprimível com 52 experiências adaptáveis para filhos de 4 a 15 anos, criando um ano de presença intencional.",
              },
              {
                n: "Bônus 5",
                title: "Plano Pai & Filho 12 Meses™",
                desc: "Livro digital e planner imprimível para planejar experiências, rituais, prioridades, acompanhamento do vínculo e registro de memórias durante 12 meses.",
              },
            ].map((b) => (
              <div
                key={b.title}
                className="flex gap-5 rounded-sm border border-border bg-card p-6 transition-all hover:border-gold/60 hover:shadow-premium md:p-8"
              >
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full gradient-gold">
                  <Gift className="h-5 w-5 text-navy-deep" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold">{b.n}</span>
                    <span className="font-sans text-[11px] text-muted-foreground">Valor: R$ 107,60</span>
                  </div>
                  <h3 className="mt-2 font-display text-xl text-navy md:text-2xl">{b.title}</h3>
                  <p className="mt-2 text-sm font-light text-muted-foreground">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARA QUEM É */}
      <section className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <SectionLabel>Este Treinamento É Para Você Se...</SectionLabel>
          <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
            Feito para <span className="italic text-gold">homens como você</span>
          </h2>

          <ul className="mx-auto mt-12 max-w-xl space-y-5 text-left">
            {[
              "Pais com filhos entre 4 e 15 anos",
              "Empresários e profissionais ocupados",
              "Homens que desejam fortalecer sua influência",
              "Pais que querem ser lembrados por mais do que dinheiro",
              "Homens que desejam construir um legado familiar",
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-4 border-b border-border/60 pb-5 text-base text-navy/85 md:text-lg"
              >
                <Check className="mt-1 h-5 w-5 flex-shrink-0 text-gold" strokeWidth={2.5} />
                <span className="font-light">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* GARANTIA */}
      <section className="bg-navy-deep px-6 py-24 text-ivory md:py-28">
        <div className="mx-auto max-w-3xl">
          <div className="grid items-center gap-12 md:grid-cols-[auto_1fr]">
            <div className="mx-auto flex h-40 w-40 flex-shrink-0 items-center justify-center rounded-full border-2 border-gold bg-navy/40 shadow-gold md:h-48 md:w-48">
              <div className="text-center">
                <ShieldCheck className="mx-auto h-10 w-10 text-gold" strokeWidth={1.5} />
                <div className="mt-2 font-display text-3xl text-gradient-gold md:text-4xl">7</div>
                <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold">
                  Dias
                </div>
              </div>
            </div>
            <div className="text-center md:text-left">
              <SectionLabel>Risco Zero</SectionLabel>
              <h2 className="font-display text-3xl font-light text-ivory md:text-4xl">
                Garantia de <span className="italic text-gold">7 Dias</span>
              </h2>
              <p className="mt-6 text-base font-light leading-relaxed text-ivory/80 md:text-lg">
                Entre, assista às primeiras aulas e conheça o método. Se perceber que o treinamento
                não é para você, basta solicitar o reembolso dentro do prazo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OFERTA */}
      <section id="checkout" className="bg-background px-6 py-24 md:py-32">
        <div className="mx-auto max-w-2xl">
          <div className="text-center">
            <SectionLabel>A Decisão</SectionLabel>
            <h2 className="font-display text-3xl font-light leading-tight text-navy md:text-5xl">
              Tudo o que <span className="italic text-gold">você recebe</span>
            </h2>
          </div>

          <div className="mt-12 rounded-sm border border-border bg-card p-8 shadow-premium md:p-12">
            <ul className="space-y-4 text-navy/90">
              <li className="flex justify-between border-b border-border/60 pb-3">
                <span className="font-light">Treinamento Completo</span>
                <span className="font-medium">R$ 497</span>
              </li>
              <li className="flex justify-between border-b border-border/60 pb-3">
                <span className="font-light">5 Bônus Exclusivos</span>
                <span className="font-medium">R$ 538</span>
              </li>
              <li className="flex justify-between pt-2 text-lg">
                <span className="font-display">Valor Total</span>
                <span className="font-display line-through opacity-60">R$ 1.035</span>
              </li>
            </ul>

            <div className="my-8 flex items-center gap-4">
              <span className="h-px flex-1 bg-gold/40" />
              <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold">
                Hoje por apenas
              </span>
              <span className="h-px flex-1 bg-gold/40" />
            </div>

            <div className="text-center">
              <div className="flex items-baseline justify-center gap-2">
                <span className="font-display text-2xl text-navy/70">R$</span>
                <span className="font-display text-7xl font-medium text-gradient-gold md:text-8xl">
                  497
                </span>
              </div>
              <p className="mt-3 text-sm font-light text-muted-foreground">
                ou <span className="font-medium text-navy">12x de R$ 51,40</span> no cartão
              </p>

              <div className="mt-8">
                <GoldButton large>Quero começar agora</GoldButton>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-gold" /> Pagamento Seguro
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-gold" /> Acesso Imediato
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-gold" /> +1.000 pais
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL */}
      <section className="relative overflow-hidden bg-navy-deep px-6 py-28 text-ivory md:py-40">
        <img
          src={fatherSon1600}
          srcSet={`${fatherSon1024} 1024w, ${fatherSon1600} 1600w, ${fatherSon1920} 1920w`}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/85 to-navy-deep/60" />
        <div className="relative z-10 mx-auto max-w-3xl text-center">
          <h2 className="font-display text-4xl font-light leading-[1.1] md:text-6xl">
            Seu filho não precisa
            <br />
            de um pai <span className="italic">perfeito</span>.
          </h2>
          <p className="mt-6 font-display text-2xl italic text-gradient-gold md:text-3xl">
            Precisa de um pai memorável.
          </p>
          <p className="mx-auto mt-10 max-w-xl text-base font-light leading-relaxed text-ivory/80 md:text-lg">
            O tempo vai passar de qualquer forma. A única pergunta é:
            <br />
            <span className="text-gold">o que seu filho vai lembrar sobre você?</span>
          </p>
          <div className="mt-12">
            <GoldButton large>Quero ser um pai memorável</GoldButton>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy-deep border-t border-gold/20 px-6 py-10 text-center text-xs text-ivory/70">
        <p className="font-display text-lg italic text-gold">O Pai que Seu Filho Vai Lembrar™</p>
        <p className="mt-3">© {new Date().getFullYear()} — Todos os direitos reservados.</p>
      </footer>

      {/* CTA FIXO MOBILE */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-gold/30 bg-navy-deep/95 p-3 backdrop-blur-md md:hidden">
        <button
          type="button"
          onClick={openLeadModal}
          data-event="cta_comecar_agora"
          className="flex w-full items-center justify-center gap-2 rounded-sm gradient-gold px-4 py-3.5 font-sans text-xs font-semibold uppercase tracking-[0.18em] text-navy-deep shadow-gold"
        >
          Quero começar agora <span>→</span>
        </button>
      </div>
      <div className="h-20 md:hidden" aria-hidden="true" />

      <Suspense fallback={null}>
        {modalOpen && <LeadCaptureModal open={modalOpen} onClose={() => setModalOpen(false)} />}
      </Suspense>
    </main>
  );
}
